/**
 * Gift with purchase — auto-adds a free gift once the cart qualifies.
 *
 * Qualification is configured per instance, either way round:
 *   data-qualifying-handle  a single product must be in the cart (cheap: the cart
 *                           API already returns each line's handle, no extra fetch)
 *   data-qualifying-tag     any product carrying the tag counts (needs a product
 *                           fetch per handle, since the cart API omits tags)
 * data-threshold sets how many qualifying units are needed. The gift is added once,
 * quantity 1, and removed again if the cart drops back below the threshold.
 *
 * IMPORTANT — pricing is NOT handled here. This element only adds and removes the
 * gift line as a UX convenience. The gift is made free either by being priced at 0
 * or by an automatic Shopify discount, and the discount is what enforces the offer
 * server-side. Client-side logic cannot be trusted to decide what something costs.
 *
 * The size of the run is capped by the gift variant's inventory, not by anything
 * here: once Shopify refuses the add, the promotion is over for everyone.
 */

// Global lock to prevent multiple instances (cart page + drawer) from adding gifts simultaneously
window.__freeGiftPromoLock = window.__freeGiftPromoLock || false;

if (!customElements.get('promotion-free-gift')) {
  customElements.define(
    'promotion-free-gift',
    class PromotionFreeGift extends HTMLElement {
      constructor() {
        super();

        // State management
        this.isProcessing = false;
        this.pendingEvaluation = false;
      }

      /**
       * Config is read here rather than in the constructor: the constructor runs the
       * moment the element is created, which for a script-created element is before
       * any attribute has been set.
       */
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        // Configuration from data attributes
        this.giftVariantId = this.dataset.giftVariantId;
        this.promoId = this.dataset.promoId || 'free-gift';
        this.threshold = parseInt(this.dataset.threshold, 10) || 1;

        // Handle mode wins when both are set — it is exact and needs no extra fetch.
        this.qualifyingHandle = this.dataset.qualifyingHandle || null;
        this.qualifyingTag = this.qualifyingHandle ? null : this.dataset.qualifyingTag || null;

        // Shared across instances (cart page + drawer) so one sold-out response stops
        // both of them retrying the add on every subsequent cart update.
        if (!window.__freeGiftSoldOut) {
          window.__freeGiftSoldOut = {};
        }
        if (this.dataset.giftSoldOut === 'true') {
          window.__freeGiftSoldOut[this.promoId] = true;
        }

        // Cache for product tag lookups (shared across instances)
        if (!window.__promoProductTagCache) {
          window.__promoProductTagCache = {};
        }
        this.productTagCache = window.__promoProductTagCache;

        this.init();
      }

      get isSoldOut() {
        return window.__freeGiftSoldOut[this.promoId] === true;
      }

      /**
       * A gift line is identified by the promo property, not by the variant id, so
       * that swapping the configured gift still lets us clean up the old line.
       */
      isGiftLine(item) {
        return Boolean(item.properties && item.properties._free_gift === this.promoId);
      }

      init() {
        // Subscribe to cart updates
        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            // Prevent infinite loops from our own updates
            if (event.source === 'free-gift-promotion') {
              return;
            }
            this.evaluateCart(event.cartData);
          });

          // Initial evaluation on page load (with small delay to prevent race with other instances)
          setTimeout(() => this.fetchCartAndEvaluate(), 100);
        } else {
          console.warn('[PromotionFreeGift] PubSub system not available');
        }
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
          this.cartUpdateUnsubscriber = undefined;
        }
        // Cart section refreshes can move this element; let a re-attach resubscribe.
        this.initialised = false;
      }

      async fetchCartAndEvaluate() {
        try {
          const response = await fetch('/cart.js');
          const cartData = await response.json();
          await this.evaluateCart(cartData);
        } catch (error) {
          console.error('[PromotionFreeGift] Error fetching cart:', error);
        }
      }

      async evaluateCart(cartData) {
        // Debounce/queue mechanism to prevent race conditions
        if (this.isProcessing || window.__freeGiftPromoLock) {
          this.pendingEvaluation = true;
          return;
        }

        this.isProcessing = true;

        try {
          const { qualifyingCount, giftItem } = await this.analyzeCart(cartData);
          const shouldHaveGift = qualifyingCount >= this.threshold;
          const hasGift = giftItem !== null;

          if (shouldHaveGift && !hasGift && !this.isSoldOut) {
            await this.addGift();
          } else if (!shouldHaveGift && hasGift) {
            await this.removeGift(giftItem);
          } else if (hasGift && giftItem.quantity > 1) {
            // One per order. We only ever add a single gift, so a larger quantity got
            // there some other way — clamp it rather than hand over the extras, which
            // matters most when the gift is priced at zero and nothing downstream
            // would charge for them.
            await this.setGiftQuantity(giftItem, 1);
          }
        } catch (error) {
          console.error('[PromotionFreeGift] Error evaluating cart:', error);
        } finally {
          this.isProcessing = false;

          // Process any pending evaluation
          if (this.pendingEvaluation) {
            this.pendingEvaluation = false;
            setTimeout(() => this.fetchCartAndEvaluate(), 200);
          }
        }
      }

      async analyzeCart(cartData) {
        let qualifyingCount = 0;
        let giftItem = null;

        if (!cartData || !cartData.items) {
          return { qualifyingCount, giftItem };
        }

        // Tag mode only: warm the cache for handles we have not looked up yet.
        if (this.qualifyingTag) {
          const productHandles = new Set();

          for (const item of cartData.items) {
            if (this.isGiftLine(item)) continue;
            if (item.handle && !this.productTagCache[item.handle]) {
              productHandles.add(item.handle);
            }
          }

          await this.fetchProductTags(Array.from(productHandles));
        }

        for (const item of cartData.items) {
          if (this.isGiftLine(item)) {
            giftItem = item;
            continue;
          }

          if (this.qualifyingHandle) {
            if (item.handle === this.qualifyingHandle) {
              qualifyingCount += item.quantity;
            }
          } else if (this.qualifyingTag) {
            const tags = this.productTagCache[item.handle];
            if (tags && tags.includes(this.qualifyingTag)) {
              qualifyingCount += item.quantity;
            }
          }
        }

        return { qualifyingCount, giftItem };
      }

      async fetchProductTags(handles) {
        if (handles.length === 0) return;

        // Fetch product info for each handle in parallel
        const fetchPromises = handles.map(async (handle) => {
          try {
            const response = await fetch(`/products/${handle}.js`);
            if (response.ok) {
              const product = await response.json();
              // Cache the tags (they come as an array of strings)
              this.productTagCache[handle] = product.tags || [];
            }
          } catch (error) {
            console.warn(`[PromotionFreeGift] Could not fetch product ${handle}:`, error);
            this.productTagCache[handle] = [];
          }
        });

        await Promise.all(fetchPromises);
      }

      async addGift() {
        // Acquire global lock to prevent multiple adds
        if (window.__freeGiftPromoLock) {
          return;
        }
        window.__freeGiftPromoLock = true;

        try {
          // Re-fetch cart to get latest state and prevent duplicates
          const checkResponse = await fetch('/cart.js');
          const checkCart = await checkResponse.json();

          // Check if gift already exists
          const existingGift = checkCart.items.find((item) => this.isGiftLine(item));

          if (existingGift) {
            // Gift already in cart, don't add again
            return;
          }

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: [
                {
                  id: this.giftVariantId,
                  quantity: 1,
                  properties: {
                    _free_gift: this.promoId,
                  },
                },
              ],
            }),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const reason = `${errorData.description || ''} ${errorData.message || ''}`;

            // Shopify refuses the add once the gift inventory is gone — that refusal
            // is what caps the run. Record it so we stop asking on every cart update,
            // and re-render the cart so the sold out message takes over.
            if (response.status === 422 || /not available|sold out|stock/i.test(reason)) {
              window.__freeGiftSoldOut[this.promoId] = true;
              this.refreshCartSections();
              return;
            }

            throw new Error(errorData.message || 'Failed to add gift');
          }

          // Publish cart update event
          const cartResponse = await fetch('/cart.js');
          const cartData = await cartResponse.json();

          if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'free-gift-promotion',
              cartData: cartData,
            });
          }

          // Refresh cart sections
          this.refreshCartSections();
        } catch (error) {
          console.error('[PromotionFreeGift] Error adding gift:', error);
        } finally {
          // Release lock after a delay to prevent rapid re-adds
          setTimeout(() => {
            window.__freeGiftPromoLock = false;
          }, 500);
        }
      }

      removeGift(giftItem) {
        return this.setGiftQuantity(giftItem, 0);
      }

      async setGiftQuantity(giftItem, quantity) {
        try {
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: giftItem.key,
              quantity,
            }),
          });

          if (!response.ok) {
            throw new Error(`Failed to set gift quantity to ${quantity}`);
          }

          const cartData = await response.json();

          // Publish cart update event
          if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'free-gift-promotion',
              cartData: cartData,
            });
          }

          // Refresh cart sections
          this.refreshCartSections();
        } catch (error) {
          console.error('[PromotionFreeGift] Error updating gift line:', error);
        }
      }

      refreshCartSections() {
        // Trigger a cart drawer/page refresh by fetching updated sections
        const cartDrawerItems = document.querySelector('cart-drawer-items');
        const cartItems = document.querySelector('cart-items');

        if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
          cartDrawerItems.onCartUpdate();
        }

        if (cartItems && typeof cartItems.onCartUpdate === 'function') {
          cartItems.onCartUpdate();
        }
      }
    }
  );
}
